/**
 * AN-ACC Assessment Component
 * Clinical assessment tool for Australian National Aged Care Classification
 */

import React, { useState, useEffect } from 'react';
import { 
  Activity, Calculator, TrendingUp, AlertTriangle, CheckCircle, 
  Save, RefreshCw, FileText, DollarSign 
} from 'lucide-react';
import ANACCService from '../../services/api/ANACCService';
import { 
  RUG_ADL_SCORES, 
  ROCKWOOD_FRAILTY_SCALE, 
  ANACC_CLASSES 
} from '../../data/models/ANACCAssessment';
import { useToast } from '../Common/ToastProvider';

const ANACCAssessment = ({ residentId }) => {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [assessment, setAssessment] = useState(null);
  const [projectedRevenue, setProjectedRevenue] = useState(null);

  useEffect(() => {
    if (residentId) {
      loadAssessment();
    }
  }, [residentId]);

  const loadAssessment = async () => {
    setLoading(true);
    try {
      const result = await ANACCService.getByResidentId(residentId);
      if (result.success && result.data) {
        setAssessment(result.data);
        calculateRevenue(result.data);
      } else {
        // Create new assessment
        setAssessment({
          residentId,
          assessmentType: 'shadow',
          rugAdl: { bedMobility: null, toileting: null, transfer: null, eating: null },
          rockwoodFrailtyScale: null,
          bradenScale: null,
          demmiScore: null,
          oxygenTherapy: false,
          enteralFeeding: false,
          complexWoundManagement: false,
          status: 'draft'
        });
      }
    } catch (error) {
      console.error('Error loading assessment:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateRevenue = (assessmentData) => {
    // Simplified revenue calculation based on AN-ACC class
    if (assessmentData.anaccClass) {
      const baseRate = 200; // Base daily rate
      const nwcWeightings = {
        1: 0.5, 2: 0.7, 3: 0.9, 4: 1.1, 5: 1.3,
        6: 1.5, 7: 1.7, 8: 1.9, 9: 2.1, 10: 2.3,
        11: 2.5, 12: 2.7, 13: 3.0
      };
      const weighting = nwcWeightings[assessmentData.anaccClass] || 1.0;
      const projected = baseRate * weighting;
      setProjectedRevenue(projected);
    }
  };

  const handleChange = (field, value) => {
    setAssessment(prev => ({
      ...prev,
      [field]: value,
      updatedAt: new Date().toISOString()
    }));
  };

  const handleRUGADLChange = (field, value) => {
    setAssessment(prev => {
      const newRugAdl = { ...prev.rugAdl, [field]: parseInt(value) };
      const total = Object.values(newRugAdl).reduce((sum, val) => sum + (val || 0), 0);
      return {
        ...prev,
        rugAdl: newRugAdl,
        rugAdlTotal: total,
        updatedAt: new Date().toISOString()
      };
    });
  };

  const calculateANACCClass = () => {
    if (!assessment) return null;
    
    // Simplified classification logic
    const { rugAdl, rockwoodFrailtyScale, bradenScale, demmiScore, 
            oxygenTherapy, enteralFeeding, complexWoundManagement } = assessment;
    
    const rugTotal = assessment.rugAdlTotal || 0;
    const frailty = rockwoodFrailtyScale || 5;
    const braden = bradenScale || 20;
    const demmi = demmiScore || 50;
    
    let classScore = 1;
    
    // RUG-ADL impact
    if (rugTotal >= 18) classScore += 3;
    else if (rugTotal >= 12) classScore += 2;
    else if (rugTotal >= 6) classScore += 1;
    
    // Frailty impact
    if (frailty >= 7) classScore += 2;
    else if (frailty >= 5) classScore += 1;
    
    // Technical requirements
    if (oxygenTherapy || enteralFeeding || complexWoundManagement) classScore += 2;
    
    // Mobility
    if (demmi < 30) classScore += 1;
    if (braden < 12) classScore += 1;
    
    const anaccClass = Math.min(Math.max(classScore, 1), 13);
    return anaccClass;
  };

  const handleSave = async () => {
    if (!assessment) return;
    
    setSaving(true);
    try {
      const anaccClass = calculateANACCClass();
      const assessmentToSave = {
        ...assessment,
        anaccClass,
        anaccClassDescription: ANACC_CLASSES[anaccClass],
        classificationDate: new Date().toISOString(),
        classificationSource: 'shadow',
        status: 'completed'
      };
      
      calculateRevenue(assessmentToSave);
      
      // Use save() method which handles both create and update logic
      const result = await ANACCService.save(assessmentToSave);
      
      if (result.success) {
        setAssessment(result.data);
        showToast('Assessment saved successfully!', 'success');
      } else {
        showToast(`Error saving assessment: ${result.error || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      console.error('Error saving assessment:', error);
      showToast('Error saving assessment', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!assessment) return null;

  const anaccClass = calculateANACCClass();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-xl shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-2">AN-ACC Assessment</h2>
            <p className="text-blue-100">Australian National Aged Care Classification</p>
          </div>
          <Activity className="w-12 h-12 text-white opacity-80" />
        </div>
      </div>

      {/* Projected Classification */}
      {anaccClass && (
        <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-6 border border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Projected AN-ACC Class</h3>
              <p className="text-3xl font-bold text-green-700">Class {anaccClass}</p>
              <p className="text-sm text-gray-600 mt-1">{ANACC_CLASSES[anaccClass]}</p>
            </div>
            {projectedRevenue && (
              <div className="text-right">
                <p className="text-sm text-gray-600">Projected Daily Subsidy</p>
                <p className="text-2xl font-bold text-green-700">
                  ${projectedRevenue.toFixed(2)}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* RUG-ADL Section */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Activity className="w-6 h-6 text-blue-600" />
          Physical Function (RUG-ADL)
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { key: 'bedMobility', label: 'Bed Mobility' },
            { key: 'toileting', label: 'Toileting' },
            { key: 'transfer', label: 'Transfer' },
            { key: 'eating', label: 'Eating' }
          ].map(({ key, label }) => (
            <div key={key}>
              <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
              <select
                value={assessment.rugAdl[key] || ''}
                onChange={(e) => handleRUGADLChange(key, e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select score</option>
                <option value="1">1 - Independent</option>
                <option value="2">2 - Supervision</option>
                <option value="3">3 - Limited Assistance</option>
                <option value="4">4 - Extensive Assistance</option>
                <option value="5">5 - Dependent</option>
              </select>
            </div>
          ))}
        </div>
        {assessment.rugAdlTotal > 0 && (
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm font-medium text-blue-900">
              Total RUG-ADL Score: <span className="text-lg font-bold">{assessment.rugAdlTotal}</span>
            </p>
          </div>
        )}
      </div>

      {/* Frailty & Pressure Care */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Calculator className="w-6 h-6 text-purple-600" />
          Frailty & Pressure Care
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rockwood Frailty Scale
            </label>
            <select
              value={assessment.rockwoodFrailtyScale || ''}
              onChange={(e) => handleChange('rockwoodFrailtyScale', parseInt(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select level</option>
              <option value="1">1 - Very Fit</option>
              <option value="2">2 - Well</option>
              <option value="3">3 - Managing Well</option>
              <option value="4">4 - Vulnerable</option>
              <option value="5">5 - Mild Frailty</option>
              <option value="6">6 - Moderate Frailty</option>
              <option value="7">7 - Severe Frailty</option>
              <option value="8">8 - Very Severe Frailty</option>
              <option value="9">9 - Terminally Ill</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Braden Scale (Pressure Risk)
            </label>
            <input
              type="number"
              min="6"
              max="23"
              value={assessment.bradenScale || ''}
              onChange={(e) => handleChange('bradenScale', parseInt(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="6-23"
            />
            {assessment.bradenScale && assessment.bradenScale < 12 && (
              <p className="text-xs text-red-600 mt-1">High risk - requires intervention</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              DEMMI Score (Mobility)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              value={assessment.demmiScore || ''}
              onChange={(e) => handleChange('demmiScore', parseInt(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="0-100"
            />
          </div>
        </div>
      </div>

      {/* Technical Nursing Requirements */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <AlertTriangle className="w-6 h-6 text-orange-600" />
          Technical Nursing Requirements
        </h3>
        <div className="space-y-4">
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={assessment.oxygenTherapy || false}
              onChange={(e) => handleChange('oxygenTherapy', e.target.checked)}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
            <span className="text-gray-700 font-medium">Oxygen Therapy (Daily usage)</span>
          </label>
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={assessment.enteralFeeding || false}
              onChange={(e) => handleChange('enteralFeeding', e.target.checked)}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
            <span className="text-gray-700 font-medium">Enteral Feeding</span>
          </label>
          {assessment.enteralFeeding && (
            <div className="ml-8 grid grid-cols-3 gap-4">
              <input
                type="text"
                placeholder="Formula"
                className="px-4 py-2 border border-gray-300 rounded-lg"
              />
              <input
                type="text"
                placeholder="Delivery Method"
                className="px-4 py-2 border border-gray-300 rounded-lg"
              />
              <input
                type="text"
                placeholder="Frequency"
                className="px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          )}
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={assessment.complexWoundManagement || false}
              onChange={(e) => handleChange('complexWoundManagement', e.target.checked)}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
            <span className="text-gray-700 font-medium">Complex Wound Management</span>
          </label>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end gap-4">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl flex items-center gap-2 disabled:opacity-50"
        >
          {saving ? (
            <>
              <RefreshCw className="w-5 h-5 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-5 h-5" />
              Save Assessment
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ANACCAssessment;

